import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  NgControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { merge, ReplaySubject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';

type inputTypes =
  'color' |
  'date' |
  'datetime' |
  'datetime-local' |
  'email' |
  'month' |
  'number' |
  'password' |
  'search' |
  'tel' |
  'text' |
  'time' |
  'url' |
  'week' |
  // not applied to <input>
  'select' |
  'textarea' |
  'checkbox' |
  'switch' |
  'mat-datepicker' |
  'datetime-picker';

@Component({
  standalone: false,
  selector:    'app-input',
  templateUrl: './app-input.component.html',
  styleUrls:   ['./app-input.component.scss'],
  providers:   [
    // {
    //   provide:     NG_VALUE_ACCESSOR,
    //   multi:       true,
    //   useExisting: AppInputComponent,
    // },
    // {
    //   provide:     NG_VALIDATORS,
    //   multi:       true,
    //   useExisting: AppInputComponent,
    // },
  ]
})
export class AppInputComponent implements ControlValueAccessor, Validator, OnInit, AfterViewInit {
  @ViewChild(MatDatepicker) matDatePicker: MatDatepicker<Date>;
  @ViewChild('selectAllMatSelect') matSelectAll: MatSelect;
  @Input() label                              = '';
  @Input() requiredStar                       = false;
  @Input() type: inputTypes                   = 'text';
  @Input() inputMode: null | 'numeric'        = null;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() color: ThemePalette                = 'primary';
  @Input() submitted                          = false;
  @Input() clientTypography                   = false;
  @Input() selectAll                          = false;

  @Input() set selectOptions(value: Array<any>) {
    this.internalSelectOptions = value;
    if ( ! this.emitSearch) {
      this.searchControl.patchValue(null, {onlySelf: true, emitEvent: false});
    }
    this.filteredOptions.next(value);
  }

  @Input() selectLabel: string | Array<string> = 'label';
  @Input() selectValue                         = 'value';
  @Input() optGroupLabel: string | null        = 'name';
  @Input() optGroupProperty: string | null     = null;
  @Input() labelDescriptionField: string;
  @Input() searchable                          = false;
  @Input() emitSearch                          = false;
  @Input() emitSearchMinLength                 = 3;
  @Input() multiple                            = false;
  @Input() placeholder: string;
  @Input() readonly: boolean; // TODO: implement for all input types
  @Input() hint: string;
  @Input() prefix: string;
  @Input() prefixType: 'text' | 'icon'         = 'text';
  @Input() suffix: string;
  @Input() suffixType: 'text' | 'icon'         = 'text';
  @Input() showLabel                           = true;
  @Input() extraLabel                          = false;
  @Input() flexClass: string;
  @Input() width                               = '';
  @Input() fullWidth: boolean;
  @Input() showClear                           = true;
  @Input() indeterminate                       = false;
  @Input() labelPosition: 'before' | 'after'   = 'after';
  @Input() highlight                           = false;

  @Input() requiredError: string;
  @Input() emailError: string;
  @Input() patternError: string;
  @Input() minLengthError: string;
  @Input() maxLengthError: string;
  @Input() minError: string;
  @Input() maxError: string;
  @Input() minAmount: string;

  @Output() filterSearch: EventEmitter<string> = new EventEmitter<string>();

  public nonFormField: Array<string>              = ['checkbox', 'switch', 'mat-datepicker', 'datetime-picker', 'datetime'];
  public searchControl: UntypedFormControl          = new UntypedFormControl();
  public filteredOptions: ReplaySubject<any> = new ReplaySubject<any>(1);
  public selectAllFormControl: UntypedFormControl   = new UntypedFormControl();
  public selectAllIndeterminate              = false;
  public selectLabels                        = [];
  public labelId                             = 'app-inp-' + Math.ceil(Math.random() * 1000);
  public isFiltering                         = false;
  public componentReady: boolean;
  public datePickerReady: boolean;
  // Angular
  public internalFormControl: UntypedFormControl    = new UntypedFormControl();

  public disabled                           = false;
  private internalSelectOptions: Array<any> = [];
  private touched                           = false;

  private errorMessages = new Map<string, () => string>();

  public datetimeDateControl: UntypedFormControl = new UntypedFormControl();
  public datetimeTimeControl: UntypedFormControl = new UntypedFormControl();

  public showPicker                = false;
  public selectedDate: Date | null = null;
  public selectedHour              = 0;
  public selectedMinute            = 0;
  public formattedDateTime         = '';

  @Input() position: 'top' | 'bottom' = 'top';

  constructor(@Self() @Optional() public wrapperControl: NgControl) {
    if (this.wrapperControl) {
      this.wrapperControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.datePickerReady = true;
    }, 0);
  }

  ngOnInit(): void {
    // this.wrapperControl?.control.setValidators([this.validate.bind(this)]);
    // this.wrapperControl?.control.updateValueAndValidity();
    if (typeof this.selectLabel === 'string') {
      this.selectLabels.push(this.selectLabel);
    } else {
      this.selectLabel.forEach(label => this.selectLabels.push(label));
    }

    if (this.type === 'password' && ! this.suffixType) {
      this.suffixType = 'icon';
    }

    this.filterOptions(null);
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(searchValue => {
        if (this.emitSearch && searchValue.length >= this.emitSearchMinLength) {
          this.filterSearch.emit(searchValue);
        } else {
          this.filterOptions(searchValue);
        }
      });
    this.filteredOptions.subscribe(result => this.isFiltering = false);

    if (this.type === 'datetime') {
      merge(
        this.datetimeDateControl.valueChanges,
        this.datetimeTimeControl.valueChanges,
      ).subscribe(() => {
        this.markAsTouched();
        if (!this.disabled) {
          this.onChange(this.combineDatetime());
        }
      });
    }

    this.componentReady = true;
  }

  onTouched = () => {
  };

  onChange          = (value) => {
  };
  onValidatorChange = () => {
  };

  public get invalid(): boolean {
    return this.wrapperControl ? this.wrapperControl.invalid : false;
  }

  public get showError(): boolean {
    if ( ! this.wrapperControl) {
      return false;
    }

    const {dirty, touched} = this.wrapperControl;

    return this.invalid && (this.submitted || (dirty || touched));
  }

  public get errors(): Array<string> {
    if ( ! this.wrapperControl) {
      return [];
    }

    const {errors} = this.wrapperControl;
    return Object.keys(errors)
      .map(key => this.errorMessages.has(key) ? this.errorMessages.get(key)() : errors[key] as string || key);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internalFormControl.disable();
      this.datetimeDateControl.disable();
      this.datetimeTimeControl.disable();
    } else {
      this.internalFormControl.enable();
      this.datetimeDateControl.enable();
      this.datetimeTimeControl.enable();
    }
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.internalFormControl.setValue(obj, {emitEvent: false});
    if (this.type === 'datetime') {
      const date = obj ? new Date(obj) : null;
      this.datetimeDateControl.setValue(date, {emitEvent: false});
      this.datetimeTimeControl.setValue(date ? new Date(date) : null, {emitEvent: false});
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return undefined;
  }

  public updateControl($event: any) {
    this.markAsTouched();
    if (this.type === 'select' && this.multiple && this.selectAll) {
      this.selectAllIndeterminate = true;
    }
    if ( ! this.disabled) {
      this.onChange(
        this.type === 'mat-datepicker' && (typeof $event.toISOString === 'function') ?
          $event.toISOString() :
          $event
      );
    }
  }

  private markAsTouched() {
    if ( ! this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public displayClearButton(): boolean {
    if ( ! this.showClear || this.internalFormControl.disabled || this.readonly) {
      return false;
    }
    const value = this.internalFormControl.value;

    if (this.multiple) {
      return !!(value && value.length > 0);
    }
    return !!(value !== null && value !== undefined && value !== '');
  }

  public clearSelect($event) {
    $event.stopPropagation();
    if ( ! this.disabled && ! this.readonly) {
      const newValue = this.multiple ? [] : null;
      this.internalFormControl.patchValue(newValue);
      this.updateControl(newValue);
    }
  }

  public filterOptions(searchValue: string | null): void {
    if ( ! this.searchable || ! searchValue) {
      this.filteredOptions.next(this.internalSelectOptions);
      return;
    }

    const search = searchValue.toLowerCase();

    if (this.optGroupProperty) {
      const filteredData = this.internalSelectOptions.map(selectOptionGroup => {
        selectOptionGroup                        = {...selectOptionGroup};
        selectOptionGroup[this.optGroupProperty] = selectOptionGroup[this.optGroupProperty]
          .filter(selectOption => this.checkForKeyword(selectOption, search));
        return selectOptionGroup;
      }).filter(selectOptionGroup => selectOptionGroup[this.optGroupProperty].length);
      this.filteredOptions.next(filteredData);
    } else {
      this.filteredOptions.next(
        this.internalSelectOptions.filter(selectOption => this.checkForKeyword(selectOption, search))
      );
    }
  }

  private checkForKeyword(selectOption: any, search: string): boolean {
    let searchTarget = '';
    if (typeof this.selectLabel === 'string') {
      searchTarget = selectOption[this.selectLabel as string].toLowerCase();
    } else {
      this.selectLabel.forEach(sLabel => {
        if (selectOption[sLabel]) {
          searchTarget += selectOption[sLabel].toLowerCase();
        }
      });
    }
    return searchTarget.indexOf(search) > -1;
  }

  public startFilteringLoader($event: any) {
    if ($event) {
      this.isFiltering = true;
    }
  }

  public parseDescriptionLabel(option: any, labelDescriptionField: string): string {
    return labelDescriptionField.split('.').reduce((r, val) => {
      return r ? r[val] : undefined;
    }, option);
  }

  public toggleIcon() {
    if (this.type === 'text') {
      this.type = 'password';
    } else if (this.type === 'password') {
      this.type = 'text';
    } else {
      return;
    }
  }

  public selectAllOptions(toggle: boolean): void {
    if (toggle) {
      const values = [];
      if ( ! this.optGroupProperty) {
        this.filteredOptions.pipe(take(1)).subscribe(options => {
          options.forEach(option => {
            values.push(option[this.selectValue]);
          });
        });
      } else {
        this.filteredOptions.pipe(take(1)).subscribe(optionGroups => {
          optionGroups.forEach(optionGroup => {
            optionGroup[this.optGroupProperty].forEach(option => {
              values.push(option[this.selectValue]);
            });
          });
        });
      }
      this.internalFormControl.patchValue(values);
      this.updateControl(this.internalFormControl.value);
    } else {
      this.internalFormControl.patchValue([]);
      this.updateControl([]);
    }
    this.selectAllIndeterminate = false;
  }

  private combineDatetime(): Date | null {
    const dateVal: Date | null = this.datetimeDateControl.value;
    const timeVal: Date | null = this.datetimeTimeControl.value;
    if (!dateVal) {
      return null;
    }
    const result = new Date(dateVal);
    if (timeVal) {
      result.setHours(timeVal.getHours(), timeVal.getMinutes(), timeVal.getSeconds(), 0);
    }
    return result;
  }

  public togglePicker(): void {
    this.showPicker = !this.showPicker;
  }

  public incrementHour($event): void {
    $event.preventDefault();
    this.selectedHour = (this.selectedHour + 1) % 24;
    this.updateDateTime();
  }

  public decrementHour($event): void {
    $event.preventDefault();
    this.selectedHour = (this.selectedHour - 1 + 24) % 24;
    this.updateDateTime();
  }

  public incrementMinute($event): void {
    $event.preventDefault();
    this.selectedMinute = (this.selectedMinute + 1) % 60;
    this.updateDateTime();
  }

  public decrementMinute($event): void {
    $event.preventDefault();
    this.selectedMinute = (this.selectedMinute - 1 + 60) % 60;
    this.updateDateTime();
  }

  public onDateChange(date: Date): void {
    this.selectedDate = date;
    this.updateDateTime();
  }

  public updateDateTime(): void {
    if (this.selectedDate) {
      const dateTime = new Date(this.selectedDate);
      dateTime.setHours(this.selectedHour);
      dateTime.setMinutes(this.selectedMinute);
      this.formattedDateTime = dateTime.toLocaleString();
    }
  }

  public confirmDateTime(): void {
    if (!this.selectedDate) {
      alert('Please select a date first.');
      return;
    }
    this.internalFormControl.setValue(this.formattedDateTime);
    this.showPicker = false;
  }

  public clear(): void {
    this.formattedDateTime = '';
    this.selectedMinute    = 0;
    this.selectedHour      = 0;
  }

  public cancelDateTime(): void {
    this.clear();
    this.showPicker = false;
  }
}
